import React, { useState } from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  Users,
  Shield,
  CreditCard,
  Building2,
  MapPin,
  Globe,
  Briefcase,
  Code,
  BarChart3,
  Search,
  TestTube,
  Server,
  FileSearch
} from 'lucide-react';

const GCCHireIndia = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [employeeCount, setEmployeeCount] = useState(10);
  const [indiaSalary, setIndiaSalary] = useState(25000);
  const [usSalary, setUsSalary] = useState(95000);
  const [usOverhead, setUsOverhead] = useState(30);

  const serviceFee = 599;

  // Calculate costs
  const indiaTotal = (indiaSalary * employeeCount) + (serviceFee * employeeCount * 12);
  const usTotal = (usSalary * employeeCount) + (usSalary * employeeCount * usOverhead / 100);
  const savings = usTotal - indiaTotal;
  const savingsPercent = ((savings / usTotal) * 100).toFixed(1);

  const trustedClients = [
    { name: 'Newmark', logo: '/images/ClientLogos/newmark.svg' },
    { name: 'Lument', logo: '/images/ClientLogos/lument.svg' },
    { name: 'Colliers', logo: '/images/ClientLogos/colliers.png' },
    { name: 'Acore Capital', logo: '/images/ClientLogos/acore.png' },
    { name: 'Columbia National', logo: '/images/ClientLogos/Columbia_noational.png' }
  ];

  const timeline = [
    {
      week: 'Week 1–2',
      title: 'Define & Plan',
      description: 'We work with you to define team structure, roles, compensation benchmarks, and compliance requirements for your India GCC.'
    },
    {
      week: 'Week 3–4',
      title: 'Recruit & Onboard',
      description: 'Our India recruitment team sources, screens, and onboards your talent — from data analysts and ML engineers to underwriters and developers.'
    },
    {
      week: 'Week 5+',
      title: 'Operate & Scale',
      description: 'Your GCC is live. Integrand Analytics manages payroll, compliance, office infrastructure, and HR — so you can focus on growing your business.'
    }
  ];

  const offerings = [
    {
      icon: Users,
      title: 'Talent Acquisition',
      description: 'Access India\'s deep talent pool of CRE analysts, data scientists, engineers, and underwriters. We handle end-to-end recruitment.',
      features: [
        'Sourcing from top-tier Indian universities',
        'Domain-specific CRE skill assessments',
        'Background verification and reference checks'
      ]
    },
    {
      icon: Shield,
      title: 'Compliance & Legal',
      description: 'Navigate India\'s regulatory landscape with confidence. We handle entity setup, labor law compliance, tax registrations, and statutory filings.',
      features: [
        'Entity incorporation and registration',
        'Employment contracts compliant with Indian law',
        'PF, ESI, and statutory compliance'
      ]
    },
    {
      icon: CreditCard,
      title: 'Payroll & Benefits',
      description: 'Competitive compensation packages benchmarked to the Indian market. Payroll, taxes, and benefits — processed accurately every month.',
      features: [
        'Monthly payroll processing and tax deductions',
        'Health insurance and wellness benefits',
        'Performance-based bonus structures'
      ]
    },
    {
      icon: Building2,
      title: 'Infrastructure & Operations',
      description: 'Modern office space, IT infrastructure, and day-to-day operations management. Your team gets an enterprise-grade work environment from day one.',
      features: [
        'Fully furnished office or hybrid setup',
        'IT hardware, software, and security',
        'Facilities management and admin support'
      ]
    }
  ];

  const whyIndiaStats = [
    {
      stat: '60–70%',
      title: 'Cost Savings',
      description: 'Significantly reduce operational costs compared to US/UK hiring — while maintaining world-class output quality and standards.'
    },
    {
      stat: '1,600+',
      title: 'GCCs in India',
      description: 'Over 1,600 multinational companies already operate GCCs in India, employing 1.7 million professionals across sectors.'
    },
    {
      stat: '5M+',
      title: 'STEM Graduates/Year',
      description: 'India produces over 5 million STEM graduates annually — the largest talent pipeline in the world for technical and analytical roles.'
    },
    {
      stat: '24/7',
      title: 'Follow-the-Sun Coverage',
      description: 'India\'s timezone (IST) enables true follow-the-sun operations, extending your work hours and accelerating deal turnarounds.'
    },
    {
      stat: '100%',
      title: 'Your Dedicated Team',
      description: 'Unlike outsourcing, a GCC is your own captive center. Full control over culture, processes, IP, and team development.'
    },
    {
      stat: 'CRE',
      title: 'Domain Expertise',
      description: 'Integrand Analytics\' deep CRE domain expertise means we recruit talent who already understand commercial real estate workflows and analysis.'
    }
  ];

  const comparisonTable = [
    { feature: 'Team Dedication', outsourcing: 'Shared resources', gcc: '100% dedicated team' },
    { feature: 'IP Ownership', outsourcing: 'Shared / vendor-owned', gcc: 'Full IP ownership' },
    { feature: 'Cultural Alignment', outsourcing: 'Limited integration', gcc: 'Your culture, your values' },
    { feature: 'Process Control', outsourcing: 'Vendor-managed', gcc: 'Direct management' },
    { feature: 'Domain Expertise', outsourcing: 'Generic talent pool', gcc: 'CRE-specialized hiring' },
    { feature: 'Scalability', outsourcing: 'Contract-dependent', gcc: 'Scale at your pace' },
    { feature: 'Long-term Cost', outsourcing: 'Increasing vendor fees', gcc: 'Predictable, decreasing cost' }
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Discovery & Strategy',
      description: 'We assess your needs, define roles, set compensation benchmarks, and create a tailored GCC strategy aligned with your business goals.'
    },
    {
      step: 2,
      title: 'Entity Setup & Compliance',
      description: 'We handle legal entity incorporation, regulatory registrations, and set up fully compliant employment frameworks in India.'
    },
    {
      step: 3,
      title: 'Recruit & Onboard',
      description: 'Our recruiting team sources domain-specific talent, conducts rigorous assessments, and onboards your team with all infrastructure in place.'
    },
    {
      step: 4,
      title: 'Operate & Scale',
      description: 'Ongoing payroll, compliance, HR management, and infrastructure operations. Scale your team up or down based on business needs.'
    }
  ];

  const roles = [
    { title: 'Data Analysts', subtitle: 'Financial modeling, deal analysis', icon: BarChart3 },
    { title: 'ML Engineers', subtitle: 'AI/ML models, automation', icon: Code },
    { title: 'Underwriters', subtitle: 'Loan analysis, risk assessment', icon: FileSearch },
    { title: 'Software Developers', subtitle: 'Full-stack, APIs, platforms', icon: Code },
    { title: 'Business Analysts', subtitle: 'Process optimization, reporting', icon: Briefcase },
    { title: 'QA Engineers', subtitle: 'Testing, quality assurance', icon: TestTube },
    { title: 'DevOps Engineers', subtitle: 'Cloud infrastructure, CI/CD', icon: Server },
    { title: 'Research Analysts', subtitle: 'Market research, due diligence', icon: Search }
  ];

  const faqs = [
    {
      question: 'What is a Global Capability Center (GCC)?',
      answer: 'A Global Capability Center (GCC) is a dedicated offshore office owned and operated by your company. Unlike traditional outsourcing, a GCC gives you full control over your team, processes, intellectual property, and company culture — while leveraging the cost and talent advantages of operating in India.'
    },
    {
      question: 'How long does it take to set up a GCC in India?',
      answer: 'With Integrand Analytics, you can go from planning to having your first team members onboarded in as little as 4–6 weeks. Traditional GCC setups can take 12–18 months, but our established infrastructure and processes dramatically accelerate the timeline.'
    },
    {
      question: 'Do I need to set up a legal entity in India?',
      answer: 'Integrand Analytics offers flexible models. We can help you set up your own entity in India, or we can serve as your Employer of Record (EOR) initially while you scale — allowing you to transition to a full entity when the time is right.'
    },
    {
      question: 'What kind of roles can I hire for?',
      answer: 'We specialize in CRE-focused roles including data analysts, financial modelers, ML engineers, underwriters, software developers, QA engineers, DevOps engineers, business analysts, and research analysts. We can also hire for operational roles like HR, finance, and admin.'
    },
    {
      question: 'How does Integrand Analytics ensure quality of hires?',
      answer: 'Our recruitment process includes domain-specific assessments, technical interviews, CRE case study evaluations, and rigorous background checks. We source from top universities (IITs, IIMs, NITs) and experienced professionals with CRE industry background.'
    },
    {
      question: 'What are the cost savings compared to hiring in the US?',
      answer: 'Companies typically see 60–70% cost savings on total compensation (salary + benefits + overhead) when hiring in India compared to equivalent US roles. This includes fully loaded costs with office space, benefits, and management overhead factored in.'
    }
  ];

  const stats = [
    { value: '500+', label: 'CRE professionals placed' },
    { value: '50+', label: 'Enterprise clients served' },
    { value: '60–70%', label: 'Average cost savings' },
    { value: '95%', label: 'Employee retention rate' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#005B63]/5 to-transparent" />
          <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-gradient-to-tr from-[#005B63]/5 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-[#005B63]/10 rounded-full text-[#005B63] text-sm font-medium mb-6">
              <Globe className="w-4 h-4 mr-2" />
              Global Capability Centers
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Build Your GCC in India with{' '}
              <span className="text-[#005B63]">Integrand Analytics</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
              Hire top-tier remote talent in India and set up your dedicated Global Capability Center — without the complexity of establishing a local entity. From hiring to compliance, Integrand Analytics handles it all.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://calendar.app.google/ugMbuFr7Wm9skbt17" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#005B63] text-white rounded-lg font-medium hover:bg-[#007A85] transition-colors group">
                Schedule a Consultation
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white border-2 border-gray-200 text-gray-700 rounded-lg font-medium hover:border-[#005B63] hover:text-[#005B63] transition-colors"
              >
                See How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted Clients */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-500 text-sm mb-8">Trusted by leading CRE firms worldwide</p>
          <div className="flex flex-wrap justify-center items-center gap-10 md:gap-16">
            {trustedClients.map((client, index) => (
              <img
                key={index}
                src={client.logo}
                alt={client.name}
                className="h-8 md:h-10 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              From decision to fully operational in weeks
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Setting up a GCC used to take 12–18 months. With Integrand Analytics, your India team is up and running in a matter of weeks.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {timeline.map((item, index) => (
              <div key={index} className="relative">
                {index < timeline.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-[#005B63] to-[#005B63]/20 -translate-x-1/2 z-0" />
                )}
                <div className="relative bg-white p-8 rounded-2xl border border-gray-200 hover:border-[#005B63]/50 hover:shadow-lg transition-all h-full">
                  <span className="inline-block px-4 py-2 bg-[#005B63] text-white rounded-full text-sm font-medium mb-4">
                    {item.week}
                  </span>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              End-to-end GCC setup and management
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Everything you need to build, operate, and scale a high-performing team in India — delivered as a fully managed service.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {offerings.map((offering, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl border border-gray-200 hover:border-[#005B63]/50 hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-[#005B63] rounded-xl flex items-center justify-center mb-6">
                  <offering.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{offering.title}</h3>
                <p className="text-gray-600 mb-6">{offering.description}</p>
                <ul className="space-y-3">
                  {offering.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#005B63] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why India GCC */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              The strategic advantage of a GCC in India
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              India is the world's #1 destination for Global Capability Centers — and for good reason.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyIndiaStats.map((item, index) => (
              <div key={index} className="bg-[#005B63] p-8 rounded-2xl hover:bg-[#007A85] transition-colors shadow-lg">
                <div className="text-4xl font-bold text-white mb-2">{item.stat}</div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-white/80 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              GCC vs. traditional outsourcing
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              See why a dedicated GCC delivers more control, better quality, and long-term strategic value.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Feature</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-500">Traditional Outsourcing</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#005B63]">GCC with Integrand Analytics</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {comparisonTable.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{row.feature}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{row.outsourcing}</td>
                      <td className="px-6 py-4 text-sm text-[#005B63] font-medium">
                        <span className="flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          {row.gcc}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Calculator */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              See how much you can save
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Compare the total cost of hiring remote employees in India through Integrand Analytics versus hiring full-time employees in the US.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Calculator Inputs */}
            <div className="bg-gray-50 p-8 rounded-2xl">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Configure Your Team</h3>
              <p className="text-gray-600 text-sm mb-8">Adjust the inputs below to estimate your total spend.</p>

              <div className="space-y-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Number of Remote Employees
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={employeeCount}
                    onChange={(e) => setEmployeeCount(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#005B63]"
                  />
                  <div className="flex justify-between mt-2">
                    <span className="text-sm text-gray-500">1</span>
                    <span className="text-lg font-bold text-[#005B63]">{employeeCount}</span>
                    <span className="text-sm text-gray-500">100</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Avg. India Salary (annual, USD)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={indiaSalary}
                        onChange={(e) => setIndiaSalary(parseInt(e.target.value) || 0)}
                        className="w-full pl-8 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#005B63] focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Avg. US Salary (annual, USD)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={usSalary}
                        onChange={(e) => setUsSalary(parseInt(e.target.value) || 0)}
                        className="w-full pl-8 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#005B63] focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Integrand Analytics Service Fee</span>
                    <span className="font-semibold text-[#005B63]">${serviceFee} / employee / month</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Covers legal employment, payroll, compliance, benefits administration, and HR support. Based on EOR model.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    US Employer Overhead (% of salary)
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="20"
                      max="50"
                      value={usOverhead}
                      onChange={(e) => setUsOverhead(parseInt(e.target.value))}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#005B63]"
                    />
                    <span className="text-lg font-bold text-gray-700 w-16 text-right">{usOverhead}%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Includes benefits, insurance, payroll taxes, 401k, office, equipment. US average is 25–40%.
                  </p>
                </div>
              </div>
            </div>

            {/* Results */}
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Annual Cost Comparison</h3>

              <div className="space-y-6">
                {/* India Option */}
                <div className="bg-[#005B63] p-6 rounded-2xl border-2 border-[#005B63] shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">&#127470;&#127475;</span>
                    <span className="font-semibold text-white">Remote Hire via Integrand Analytics</span>
                  </div>
                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex justify-between">
                      <span className="text-white/80">Total salaries</span>
                      <span className="font-medium text-white">${(indiaSalary * employeeCount).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/80">Integrand Analytics fees (${serviceFee} x {employeeCount} x 12 mo)</span>
                      <span className="font-medium text-white">${(serviceFee * employeeCount * 12).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-4 border-t border-white/20">
                    <span className="font-semibold text-white">Total Annual Spend</span>
                    <span className="text-2xl font-bold text-white">${indiaTotal.toLocaleString()}</span>
                  </div>
                </div>

                <div className="text-center text-gray-400 font-medium">vs</div>

                {/* US Option */}
                <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">&#127482;&#127480;</span>
                    <span className="font-semibold text-gray-900">Full-Time US Hire</span>
                  </div>
                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total salaries</span>
                      <span className="font-medium">${(usSalary * employeeCount).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Employer overhead ({usOverhead}%)</span>
                      <span className="font-medium">${(usSalary * employeeCount * usOverhead / 100).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                    <span className="font-semibold text-gray-900">Total Annual Spend</span>
                    <span className="text-2xl font-bold text-gray-700">${usTotal.toLocaleString()}</span>
                  </div>
                </div>

                {/* Savings */}
                <div className="bg-gradient-to-r from-[#005B63] to-[#007A85] p-6 rounded-2xl text-white">
                  <div className="text-center">
                    <p className="text-white/80 mb-2">Your Annual Savings</p>
                    <p className="text-4xl font-bold mb-2">${savings.toLocaleString()}</p>
                    <p className="text-lg text-white/90">{savingsPercent}% less with Integrand Analytics</p>
                  </div>
                </div>

                <a href="https://calendar.app.google/ugMbuFr7Wm9skbt17" target="_blank" rel="noopener noreferrer" className="w-full py-4 bg-[#005B63] text-white rounded-lg font-medium hover:bg-[#007A85] transition-colors flex items-center justify-center gap-2">
                  Get a Custom Quote
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Your GCC, our expertise
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We handle the heavy lifting so you can focus on what matters — growing your business.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {howItWorks.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white p-6 rounded-2xl border border-gray-200 hover:border-[#005B63]/50 hover:shadow-lg transition-all h-full">
                  <div className="w-12 h-12 bg-[#005B63] text-white rounded-xl flex items-center justify-center text-xl font-bold mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Your Global Team */}
          <div className="bg-white p-8 md:p-12 rounded-2xl border border-gray-200">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Your Global Team, Managed Locally</h3>
                <p className="text-gray-600 mb-6">
                  Integrand Analytics operates on the ground in India — providing local expertise with global standards.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-[#005B63]" />
                    <span className="text-gray-700">Bangalore & Hyderabad offices</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-[#005B63]" />
                    <span className="text-gray-700">SOC 2 compliant infrastructure</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-[#005B63]" />
                    <span className="text-gray-700">Dedicated account management</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center p-6 bg-gray-50 rounded-xl">
                    <div className="text-3xl font-bold text-[#005B63] mb-1">{stat.value}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Roles We Hire */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              CRE-specialized talent, ready to scale
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From analytics to underwriting — we recruit professionals who understand commercial real estate from day one.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {roles.map((role, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 hover:border-[#005B63] hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-[#005B63] rounded-lg flex items-center justify-center mb-4">
                  <role.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-1">{role.title}</h3>
                <p className="text-sm text-gray-700">{role.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently asked questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about setting up a GCC with Integrand Analytics.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#005B63] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5 text-gray-600">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#005B63]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to build your India team?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Schedule a free consultation with our GCC experts. We'll help you build a business case and a roadmap tailored to your company's needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://calendar.app.google/ugMbuFr7Wm9skbt17" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#005B63] rounded-lg font-medium hover:bg-gray-100 transition-colors group">
              Schedule a Consultation
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="/"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-medium hover:bg-white/10 transition-colors"
            >
              Visit Integrand Analytics
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GCCHireIndia;
